import { writable, get } from "svelte/store";
import type { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import type {
  SendCartLinesAddMutation,
  GetCartQuery,
  Cart,
  CartLineUpdateInput,
  SendCartLinesUpdateMutation,
  SendCartCreateMutation,
  BaseCartLineEdge,
  SendCartBuyerIdentityUpdateMutation,
  CartBuyerIdentityInput,
} from "$lib/gql/gen/codegen";
import {
  SendCartLinesAddDoc,
  SendCartCreateDoc,
  GetCartDoc,
  SendCartLinesUpdateDoc,
  SendCartBuyerIdentityUpdateDoc,
} from "$lib/gql/gen/codegen";
import client from "$lib/gql/apolloClient";
import { browser } from "$app/environment";
import cfg from "$lib/config/config";
import { goto } from "$app/navigation";
import type { RegionStore } from "./region";
import { account } from "./account";
import { linkTo } from "$lib/utils/url";
import { pushGtmEcommerceEvent } from "$lib/utils/gtmEvent";
import { voyadoUpdateCart } from "../utils/voyado";
import { recommendations } from "./recommendations";
import { setCookie } from "$lib/utils/cookie";
import type { GetMerchandiseInventoryLevelsQuery } from "$lib/gql/genAdmin/codegen";
import { GetMerchandiseInventoryLevelsDoc } from "$lib/gql/genAdmin/codegen";

export type CartStore = ReturnType<typeof cartStore>;

export function cartStore(
  client: ApolloClient<NormalizedCacheObject>,
  localStorage?: Storage,
  pushGtmEvents = false,
) {
  const cart = writable<Cart>();

  // initialise subscription to update local storage
  if (localStorage) {
    cart.subscribe(cart => {
      if (cart?.lines?.edges) {
        localStorage.setItem(
          `${cfg.SHOPIFY_STORE}:cartItems`,
          JSON.stringify(cart?.lines?.edges),
        );
      }
    });
  }

  const initCartFromLocalStorage = async () => {
    if (
      localStorage &&
      localStorage.getItem(`${cfg.SHOPIFY_STORE}:cartItems`)
    ) {
      const items = localStorage.getItem(`${cfg.SHOPIFY_STORE}:cartItems`);
      let cartItems: any[] = [];

      if (items) {
        cartItems = JSON.parse(items).map((item: BaseCartLineEdge) => {
          const findUrl = item.node?.attributes?.find(
            attribute => attribute.key === "_url",
          );
          const itemUrl = findUrl?.value || "bag";
          const canonicalUrl =
            item.node?.merchandise?.product?.canonicalUrl?.value;

          return {
            merchandiseId: item.node.merchandise.id,
            quantity: item.node.quantity,
            attributes: [
              {
                key: "_url",
                value: canonicalUrl ? canonicalUrl : itemUrl,
              },
            ],
          };
        });
      }

      // Reinitialise a new cart with the same items
      try {
        const accessToken = get(account)?.accessToken || "";
        const email = get(account)?.email || "";
        const res = await client.mutate<SendCartCreateMutation>({
          mutation: SendCartCreateDoc,
          variables: {
            buyerIdentity: {
              customerAccessToken: accessToken,
              email: email,
            },
            input: {
              lines: cartItems,
            },
          },
        });
        if (res?.data?.cartCreate?.cart) {
          const createdCart: Cart = res.data.cartCreate.cart;
          if (localStorage) {
            localStorage.setItem(
              `${cfg.SHOPIFY_STORE}:cartId`,
              JSON.stringify(createdCart.id),
            );
          }
          setCookie(
            "cart",
            createdCart.id.split("gid://shopify/Cart/")[1],
            false,
          );

          window.Nosto &&
            window.Nosto.sendCartToken(
              createdCart.id.split("gid://shopify/Cart/")[1],
            );

          cart.set(createdCart);
          return createdCart;
        }
      } catch (error) {
        console.error(error);
        return null;
      }
    }
  };

  const updateCartOmniAttributes = async () => {
    try {
      const currentCart = get(cart);
      const response = await fetch(
        `${cfg.BASE_PATH || ""}/api/omnichannel-stock-check`,
        {
          method: "POST",
          body: JSON.stringify({ cartId: currentCart.id }),
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to update cart omnichannel attributes: ${response.statusText}`,
        );
      }
      const data = await response.json();
      if (data.error) {
        console.error(data.error);
        return null;
      }
      return data;
    } catch (error) {
      console.error("Error updating cart omnichannel attributes:", error);
      return null;
    }
  };

  return {
    subscribe: cart.subscribe,
    set: cart.set,

    createCart: async (
      merchandiseId: string,
      url: string,
      giftCardInfo?: {
        recipientEmail: string;
        recipientName?: string;
        message?: string;
        sendOn?: string;
      },
    ) => {
      try {
        const giftCard = url.includes("gift-card");

        const res = await client.mutate<SendCartCreateMutation>({
          mutation: SendCartCreateDoc,
          variables: {
            input: {
              lines: [
                {
                  merchandiseId: merchandiseId,
                  quantity: 1,
                  attributes: [
                    {
                      key: "_url",
                      value: url,
                    },
                    ...(giftCard
                      ? [
                          {
                            key: "__shopify_send_gift_card_to_recipient",
                            value: "true",
                          },
                          ...(giftCardInfo?.recipientEmail
                            ? [
                                {
                                  key: "Recipient email",
                                  value: giftCardInfo?.recipientEmail,
                                },
                              ]
                            : []),
                          ...(giftCardInfo?.recipientName
                            ? [
                                {
                                  key: "Recipient name",
                                  value: giftCardInfo?.recipientName,
                                },
                              ]
                            : []),
                          ...(giftCardInfo?.message
                            ? [{ key: "Message", value: giftCardInfo?.message }]
                            : []),
                          ...(giftCardInfo?.sendOn
                            ? [{ key: "Send on", value: giftCardInfo?.sendOn }]
                            : []),
                        ]
                      : []),
                  ],
                },
              ],
              buyerIdentity: {
                customerAccessToken: get(account)?.accessToken,
                email: get(account)?.email,
              },
            },
          },
        });
        if (res?.errors) {
          console.error("GraphQL errors:", res.errors);
        }
        if (res?.data?.cartCreate?.cart) {
          const createdCart: Cart = res.data.cartCreate.cart;
          if (localStorage) {
            localStorage.setItem(
              `${cfg.SHOPIFY_STORE}:cartId`,
              JSON.stringify(createdCart.id),
            );
            // Used for Purchase with User Account e2e test
            localStorage.setItem(
              `${cfg.SHOPIFY_STORE}:checkoutUrl`,
              JSON.stringify(createdCart.checkoutUrl),
            );
          }
          setCookie(
            "cart",
            createdCart.id.split("gid://shopify/Cart/")[1],
            false,
          );

          window.Nosto &&
            window.Nosto.sendCartToken(
              createdCart.id.split("gid://shopify/Cart/")[1],
            );

          if (pushGtmEvents) {
            pushGtmEcommerceEvent("add_to_cart", [
              createdCart?.lines?.edges[0],
            ]);
          }
          cart.set(createdCart);
          return createdCart;
        }
      } catch (error) {
        console.error(error);
        return null;
      }
    },

    cartLinesAdd: async (
      currentCartId: string,
      merchandiseId: string,
      url: string,
      regionInfo: RegionStore,
      giftCardInfo?: {
        recipientEmail: string;
        recipientName?: string;
        message?: string;
        sendOn?: string;
      },
    ) => {
      try {
        const giftCard = url.includes("gift-card");

        setCookie("cart", currentCartId.split("gid://shopify/Cart/")[1], false);

        window.Nosto &&
          window.Nosto.sendCartToken(
            currentCartId.split("gid://shopify/Cart/")[1],
          );

        const locale = get(regionInfo).locale;
        const region = get(regionInfo).region;
        const res = await client.mutate<SendCartLinesAddMutation>({
          mutation: SendCartLinesAddDoc,
          variables: {
            cartId: currentCartId,
            lines: [
              {
                merchandiseId: merchandiseId,
                quantity: 1,
                attributes: [
                  {
                    key: "_url",
                    value: url,
                  },

                  ...(giftCard
                    ? [
                        {
                          key: "__shopify_send_gift_card_to_recipient",
                          value: "true",
                        },
                        ...(giftCardInfo?.recipientEmail
                          ? [
                              {
                                key: "Recipient email",
                                value: giftCardInfo?.recipientEmail,
                              },
                            ]
                          : []),
                        ...(giftCardInfo?.recipientName
                          ? [
                              {
                                key: "Recipient name",
                                value: giftCardInfo?.recipientName,
                              },
                            ]
                          : []),
                        ...(giftCardInfo?.message
                          ? [{ key: "Message", value: giftCardInfo?.message }]
                          : []),
                        ...(giftCardInfo?.sendOn
                          ? [{ key: "Send on", value: giftCardInfo?.sendOn }]
                          : []),
                      ]
                    : []),
                ],
              },
            ],
          },
        });
        if (res?.errors) {
          console.error("GraphQL errors:", res.errors);
        }
        if (res?.data?.cartLinesAdd?.cart) {
          const updatedCart: Cart = res.data.cartLinesAdd.cart;
          const addedItem = updatedCart.lines.edges.find(
            item => item.node.merchandise.id === merchandiseId,
          );
          if (pushGtmEvents && addedItem) {
            pushGtmEcommerceEvent("add_to_cart", [addedItem]);
          }
          cart.set(updatedCart);
          recommendations.setCart();
          voyadoUpdateCart(updatedCart, region, locale ? locale : "en");
          await updateCartOmniAttributes();
          return updatedCart;
        }
      } catch (error) {
        initCartFromLocalStorage().then(async data => {
          if (!merchandiseId) return;
          try {
            const giftCard = url.includes("gift-card");

            const locale = get(regionInfo).locale;
            const region = get(regionInfo).region;
            const res = await client.mutate<SendCartLinesAddMutation>({
              mutation: SendCartLinesAddDoc,
              variables: {
                cartId: currentCartId,
                lines: [
                  {
                    merchandiseId: merchandiseId,
                    quantity: 1,
                    attributes: [
                      {
                        key: "_url",
                        value: url,
                      },
                      ...(giftCard
                        ? [
                            {
                              key: "__shopify_send_gift_card_to_recipient",
                              value: "true",
                            },
                            ...(giftCardInfo?.recipientEmail
                              ? [
                                  {
                                    key: "Recipient email",
                                    value: giftCardInfo?.recipientEmail,
                                  },
                                ]
                              : []),
                            ...(giftCardInfo?.recipientName
                              ? [
                                  {
                                    key: "Recipient name",
                                    value: giftCardInfo?.recipientName,
                                  },
                                ]
                              : []),
                            ...(giftCardInfo?.message
                              ? [
                                  {
                                    key: "Message",
                                    value: giftCardInfo?.message,
                                  },
                                ]
                              : []),
                            ...(giftCardInfo?.sendOn
                              ? [
                                  {
                                    key: "Send on",
                                    value: giftCardInfo?.sendOn,
                                  },
                                ]
                              : []),
                          ]
                        : []),
                    ],
                  },
                ],
              },
            });
            if (res?.data?.cartLinesAdd?.cart) {
              const updatedCart: Cart = res.data.cartLinesAdd.cart;
              cart.set(updatedCart);
              recommendations.setCart();
              voyadoUpdateCart(updatedCart, region, locale ? locale : "en");
              await updateCartOmniAttributes();
              return updatedCart;
            }
          } catch (err) {
            console.log("CartLinesAdd: Could not add product line:", err);
          }
        });
        return null;
      }
    },

    getCart: async () => {
      let cartId: string = "";
      if (localStorage) {
        const cartIdValue = localStorage.getItem(`${cfg.SHOPIFY_STORE}:cartId`);
        cartId = cartIdValue ? JSON.parse(cartIdValue) : "";
      }
      if (!cartId) return;
      try {
        const res = await client.query<GetCartQuery>({
          query: GetCartDoc,
          variables: {
            id: cartId,
          },
        });
        if (res?.data?.cart) {
          const cartData: Cart = res.data.cart;
          cart.set(cartData);
          await updateCartOmniAttributes();
          return cartData;
        } else {
          if (localStorage) {
            localStorage.setItem(`${cfg.SHOPIFY_STORE}:cartId`, "");
          }
          setCookie("cart", "", false);
          cart.set(null);
          await updateCartOmniAttributes();
          return null;
        }
      } catch (error) {
        console.log("Cart invalid. Reinitialising");
        initCartFromLocalStorage();
        return null;
      }
    },

    checkout: async (checkoutUrl: string, regionInfo: RegionStore) => {
      try {
        const locale = get(regionInfo).locale;
        if (get(account)?.accessToken) {
          await account.renewToken();
          if (get(account)?.accessToken) {
            try {
              const accessToken = get(account)?.accessToken || "";
              const email = get(account)?.email || "";
              await updateCartOmniAttributes();
              await fetch(
                `${
                  cfg.BASE_PATH || ""
                }/api/multipass.json?email=${encodeURIComponent(
                  email,
                )}&checkoutUrl=${encodeURI(checkoutUrl)}`,
                {
                  method: "GET",
                  headers: {
                    "X-Shopify-Storefront-Access-Token": accessToken,
                  },
                },
              )
                .then(response => {
                  if (response.status === 401) {
                    account.set(null);
                    goto(linkTo("account/login"));
                  } else {
                    return response.json().then(data => {
                      if (localStorage) {
                        localStorage.setItem(
                          `${cfg.SHOPIFY_STORE}:cartItems`,
                          "",
                        );
                      }
                      goto(data.url);
                    });
                  }
                })
                .catch(() => {
                  if (localStorage) {
                    localStorage.setItem(`${cfg.SHOPIFY_STORE}:cartItems`, "");
                  }
                  goto(checkoutUrl + (locale ? `&locale=${locale}` : ""));
                });
            } catch (error) {
              console.error(error);
            }
          }
        } else {
          if (localStorage) {
            localStorage.setItem(`${cfg.SHOPIFY_STORE}:cartItems`, "");
          }
          await updateCartOmniAttributes();
          goto(checkoutUrl + (locale ? `&locale=${locale}` : ""));
        }
      } catch (error) {
        console.error(error);
      }
    },

    setQuantity: async (
      id: string,
      lines: CartLineUpdateInput[],
      regionInfo: RegionStore,
    ) => {
      try {
        const locale = get(regionInfo).locale;
        const region = get(regionInfo).region;
        const res = await client.mutate<SendCartLinesUpdateMutation>({
          mutation: SendCartLinesUpdateDoc,
          variables: {
            cartId: id,
            lines: lines,
          },
        });
        if (res?.data?.cartLinesUpdate?.cart) {
          const currentCart = get(cart);
          const merchandiseId = lines[0].merchandiseId;
          const updatedItem = currentCart.lines.edges.find(
            item => item.node.merchandise.id === merchandiseId,
          );
          const updatedCart = res.data.cartLinesUpdate.cart;
          if (pushGtmEvents && updatedItem) {
            if (updatedCart.totalQuantity > currentCart.totalQuantity) {
              pushGtmEcommerceEvent("add_to_cart", [updatedItem]);
            } else {
              pushGtmEcommerceEvent("remove_from_cart", [updatedItem]);
            }
          }
          cart.set(updatedCart);
          recommendations.setCart();
          voyadoUpdateCart(updatedCart, region, locale ? locale : "en");
          setCookie(
            "cart",
            currentCart.id.split("gid://shopify/Cart/")[1],
            false,
          );
          window.Nosto &&
            window.Nosto.sendCartToken(
              currentCart.id.split("gid://shopify/Cart/")[1],
            );
          await updateCartOmniAttributes();
          return updatedCart;
        }
      } catch (err) {
        initCartFromLocalStorage().then(async data => {
          if (!lines) return;
          try {
            const lineToUpdate = data.lines.edges.find(
              (line: CartLineUpdateInput) =>
                line.node.merchandise.id === lines[0].merchandiseId,
            );

            const giftCard =
              lineToUpdate?.node?.attributes?.find(
                attr => attr.key === "__shopify_send_gift_card_to_recipient",
              ).value === "true";

            const giftCardInfo = {
              recipientEmail: lineToUpdate?.node?.attributes?.find(
                attr => attr.key === "Recipient email",
              )?.value,
              recipientName: lineToUpdate?.node?.attributes?.find(
                attr => attr.key === "Recipient name",
              )?.value,
              message: lineToUpdate?.node?.attributes?.find(
                attr => attr.key === "Message",
              )?.value,
              sendOn: lineToUpdate?.node?.attributes?.find(
                attr => attr.key === "Send on",
              )?.value,
            };
            const res = await client.mutate<SendCartLinesUpdateMutation>({
              mutation: SendCartLinesUpdateDoc,
              variables: {
                cartId: data.id,
                lines: [
                  {
                    id: lineToUpdate?.node?.id,
                    merchandiseId: lines[0].merchandiseId,
                    quantity: lines[0].quantity,
                    attributes: [
                      {
                        key: "_url",
                        value: lines[0]?.attributes?.find(
                          attr => attr.key === "_url",
                        ).value,
                      },
                      ...(giftCard
                        ? [
                            {
                              key: "__shopify_send_gift_card_to_recipient",
                              value: "true",
                            },
                            ...(giftCardInfo?.recipientEmail
                              ? [
                                  {
                                    key: "Recipient email",
                                    value: giftCardInfo?.recipientEmail,
                                  },
                                ]
                              : []),
                            ...(giftCardInfo?.recipientName
                              ? [
                                  {
                                    key: "Recipient name",
                                    value: giftCardInfo?.recipientName,
                                  },
                                ]
                              : []),
                            ...(giftCardInfo?.message
                              ? [
                                  {
                                    key: "Message",
                                    value: giftCardInfo?.message,
                                  },
                                ]
                              : []),
                            ...(giftCardInfo?.sendOn
                              ? [
                                  {
                                    key: "Send on",
                                    value: giftCardInfo?.sendOn,
                                  },
                                ]
                              : []),
                          ]
                        : []),
                    ],
                  },
                ],
              },
            });
            if (res?.data?.cartLinesUpdate?.cart) {
              const currentCart = get(cart);
              const updatedCart: Cart = res.data.cartLinesUpdate.cart;
              if (pushGtmEvents && lineToUpdate) {
                if (updatedCart.totalQuantity > currentCart.totalQuantity) {
                  pushGtmEcommerceEvent("add_to_cart", [lineToUpdate]);
                } else {
                  pushGtmEcommerceEvent("remove_from_cart", [lineToUpdate]);
                }
              }
              cart.set(updatedCart);
              await updateCartOmniAttributes();
              return updatedCart;
            }
          } catch (err) {
            console.log("SetQuantity: Could not update product quantity:", err);
          }
        });
        return null;
      }
    },
    buyerIdentity: async (
      currentCartId: string,
      buyerIdentity: CartBuyerIdentityInput,
    ) => {
      try {
        const res = await client.mutate<SendCartBuyerIdentityUpdateMutation>({
          mutation: SendCartBuyerIdentityUpdateDoc,
          variables: {
            cartId: currentCartId,
            buyerIdentity: buyerIdentity,
          },
        });
        if (res?.errors) {
          console.error("GraphQL errors:", res.errors);
          return null;
        }
        if (res?.data?.cartBuyerIdentityUpdate) {
          if (get(account)?.accessToken) {
            await account.renewToken();
            if (get(account)?.accessToken) {
              const updatedCart: Cart = res.data.cartBuyerIdentityUpdate.cart;
              cart.set(updatedCart);
              return updatedCart;
            }
          }
        }
      } catch (error) {
        console.error(error);
        return null;
      }
    },
  };
}
export const cart = cartStore(
  client,
  browser && navigator.cookieEnabled ? localStorage : undefined,
  true,
);
